import { calculateCheckDigit, NUMERIC_REGEX } from './check-digit.js';
import { PRIMARY_KEY_SET } from './keys.js';
function encodeBcdDigits(digits) {
    const byteLen = Math.ceil(digits.length / 2);
    const out = new Uint8Array(byteLen);
    for (let i = 0; i < byteLen; i += 1) {
        const hi = Number(digits[i * 2] ?? '0');
        const loChar = digits[i * 2 + 1];
        const lo = loChar !== undefined ? Number(loChar) : 0x0f;
        out[i] = ((hi & 0x0f) << 4) | (lo & 0x0f);
    }
    return out;
}
function decodeBcdDigits(bytes, offset, digitCount) {
    const byteLen = Math.ceil(digitCount / 2);
    if (offset + byteLen > bytes.length)
        return null;
    let out = '';
    for (let i = 0; i < byteLen; i += 1) {
        const b = bytes[offset + i] ?? 0;
        const hi = (b >> 4) & 0x0f;
        const lo = b & 0x0f;
        if (hi > 9)
            return null;
        out += String(hi);
        if (out.length < digitCount) {
            if (lo > 9)
                return null;
            out += String(lo);
        }
    }
    return out;
}
function encodeBinaryElement(ai, value) {
    const isNumeric = NUMERIC_REGEX.test(value);
    const aiBcd = encodeBcdDigits(ai);
    const valBytes = isNumeric ? encodeBcdDigits(value) : new TextEncoder().encode(value);
    // Header byte: bits 7..6 = (ai.length - 1), bit 5 = isNumeric (1=BCD, 0=UTF8), bits 4..0 = value length (1..31)
    const header = (((ai.length - 1) & 0x03) << 6) | ((isNumeric ? 1 : 0) << 5) | (value.length & 0x1f);
    const buf = new Uint8Array(1 + aiBcd.length + valBytes.length);
    buf[0] = header;
    buf.set(aiBcd, 1);
    buf.set(valBytes, 1 + aiBcd.length);
    return buf;
}
function decodeBinaryElement(bytes, offset) {
    if (offset >= bytes.length)
        return null;
    const header = bytes[offset] ?? 0;
    const aiLen = ((header >> 6) & 0x03) + 1;
    const isNumeric = ((header >> 5) & 0x01) === 1;
    const valLen = header & 0x1f;
    if (aiLen < 2 || aiLen > 4 || valLen === 0)
        return null;
    const aiByteLen = Math.ceil(aiLen / 2);
    const ai = decodeBcdDigits(bytes, offset + 1, aiLen);
    if (ai === null)
        return null;
    const valStart = offset + 1 + aiByteLen;
    if (isNumeric) {
        const valByteLen = Math.ceil(valLen / 2);
        const val = decodeBcdDigits(bytes, valStart, valLen);
        if (val === null)
            return null;
        return { ai, value: val, nextOffset: valStart + valByteLen };
    }
    if (valStart + valLen > bytes.length)
        return null;
    const value = new TextDecoder().decode(bytes.subarray(valStart, valStart + valLen));
    return { ai, value, nextOffset: valStart + valLen };
}
/**
 * Encodes a GS1 Digital Link payload into a compact EPC binary URI path (`/ex<base64url>` or `/eh<hex>`)
 * per GS1 Digital Link Standard: URI Syntax v1.7.0 §5–§6 (§6.1.2 RE3, pp43–44) and
 * GS1-Conformant Resolver Standard v1.2.1 §2.3 (p14) & §5 conformance item 5 (p34).
 */
export function encodeEpcBinaryUriPath(payload, format = 'ex') {
    const qEntries = Object.entries(payload.qualifiers ?? {});
    const aEntries = Object.entries(payload.dataAttributes ?? {});
    const pkPart = encodeBinaryElement(payload.ai, payload.primaryKey);
    const qParts = qEntries.map(([k, v]) => encodeBinaryElement(k, v));
    const aParts = aEntries.map(([k, v]) => encodeBinaryElement(k, v));
    const totalLen = 2 +
        pkPart.length +
        qParts.reduce((acc, p) => acc + p.length, 0) +
        aParts.reduce((acc, p) => acc + p.length, 0);
    const out = new Uint8Array(totalLen);
    // EPC/GS1 DL binary header 0x3B + counts byte ((qCount << 4) | aCount)
    out[0] = 0x3b;
    out[1] = ((qEntries.length & 0x0f) << 4) | (aEntries.length & 0x0f);
    let pos = 2;
    out.set(pkPart, pos);
    pos += pkPart.length;
    for (const qp of qParts) {
        out.set(qp, pos);
        pos += qp.length;
    }
    for (const ap of aParts) {
        out.set(ap, pos);
        pos += ap.length;
    }
    return format === 'eh' ? `/eh${bytesToHex(out)}` : `/ex${bytesToBase64Url(out)}`;
}
/**
 * Decodes an `/ex<base64url>` or `/eh<hex>` EPC binary path segment into its GS1 components.
 * Supports both SGTIN-96 (`0x30` header, 12 bytes) and GS1 DL binary TLV (`0x3B` header).
 * Clause: GS1-Conformant Resolver Standard v1.2.1 §2.3, p14 & §5 item 5, p34;
 * GS1 Digital Link Standard: URI Syntax v1.7.0 §6.1.2, pp43–44.
 */
export function tryDecompressEpcPathSegment(segment) {
    let bytes;
    if (segment.startsWith('ex') && segment.length > 2) {
        const b64Part = segment.slice(2);
        if (!/^[A-Za-z0-9_-]+$/.test(b64Part))
            return null;
        const decoded = base64UrlToBytes(b64Part);
        if (decoded === null || bytesToBase64Url(decoded) !== b64Part.replace(/=+$/, ''))
            return null;
        bytes = decoded;
    }
    else if (segment.startsWith('eh') && segment.length > 2) {
        const hexPart = segment.slice(2);
        if (hexPart.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(hexPart))
            return null;
        bytes = hexToBytes(hexPart);
    }
    else {
        return null;
    }
    if (bytes.length < 4)
        return null;
    const headerByte = bytes[0];
    // SGTIN-96 EPC binary header (0x30, 12 bytes = 96 bits)
    if (headerByte === 0x30 && bytes.length === 12) {
        let bits = 0n;
        for (const b of bytes) {
            bits = (bits << 8n) | BigInt(b);
        }
        const partition = Number((bits >> 82n) & 0x07n);
        const partitionTable = [
            [40, 12, 4, 1],
            [37, 11, 7, 2],
            [34, 10, 10, 3],
            [30, 9, 14, 4],
            [27, 8, 17, 5],
            [24, 7, 20, 6],
            [20, 6, 24, 7],
        ];
        const partRow = partitionTable[partition];
        if (partRow === undefined)
            return null;
        const [cpBits, cpDigits, irBits, irDigits] = partRow;
        const cpShift = BigInt(38 + irBits);
        const cpMask = (1n << BigInt(cpBits)) - 1n;
        const irMask = (1n << BigInt(irBits)) - 1n;
        const serialMask = (1n << 38n) - 1n;
        const cpVal = (bits >> cpShift) & cpMask;
        const irVal = (bits >> 38n) & irMask;
        const serialVal = bits & serialMask;
        const cpStr = cpVal.toString(10).padStart(cpDigits, '0');
        const irStr = irVal.toString(10).padStart(irDigits, '0');
        if (cpStr.length > cpDigits || irStr.length > irDigits)
            return null;
        const indicator = irStr[0] ?? '0';
        const itemRefRest = irStr.slice(1);
        const gtin13WithoutCheck = `${indicator}${cpStr}${itemRefRest}`;
        if (gtin13WithoutCheck.length !== 13)
            return null;
        const gtin14 = `${gtin13WithoutCheck}${calculateCheckDigit(gtin13WithoutCheck)}`;
        return {
            ai: '01',
            primaryKey: gtin14,
            qualifiers: { '21': serialVal.toString(10) },
        };
    }
    // GS1 DL binary bit-packed stream (0x3B header)
    if (headerByte === 0x3b) {
        const counts = bytes[1] ?? 0;
        const qCount = (counts >> 4) & 0x0f;
        const aCount = counts & 0x0f;
        const pkDecoded = decodeBinaryElement(bytes, 2);
        if (pkDecoded === null || !PRIMARY_KEY_SET.has(pkDecoded.ai)) {
            return null;
        }
        let offset = pkDecoded.nextOffset;
        const qualifiers = {};
        for (let i = 0; i < qCount; i += 1) {
            const qElem = decodeBinaryElement(bytes, offset);
            if (qElem === null)
                return null;
            qualifiers[qElem.ai] = qElem.value;
            offset = qElem.nextOffset;
        }
        const dataAttributes = {};
        for (let i = 0; i < aCount; i += 1) {
            const aElem = decodeBinaryElement(bytes, offset);
            if (aElem === null)
                return null;
            dataAttributes[aElem.ai] = aElem.value;
            offset = aElem.nextOffset;
        }
        if (offset !== bytes.length)
            return null;
        return {
            ai: pkDecoded.ai,
            primaryKey: pkDecoded.value,
            qualifiers,
            dataAttributes,
        };
    }
    return null;
}
export function bytesToHex(bytes) {
    let hex = '';
    for (const b of bytes) {
        hex += b.toString(16).padStart(2, '0');
    }
    return hex;
}
export function hexToBytes(hex) {
    const out = new Uint8Array(hex.length / 2);
    for (let i = 0; i < out.length; i += 1) {
        out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
}
const B64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
export function bytesToBase64Url(bytes) {
    let out = '';
    const len = bytes.length;
    for (let i = 0; i < len; i += 3) {
        const b0 = bytes[i] ?? 0;
        const b1 = bytes[i + 1] ?? 0;
        const b2 = bytes[i + 2] ?? 0;
        const triple = (b0 << 16) | (b1 << 8) | b2;
        out += B64URL_ALPHABET.charAt((triple >> 18) & 0x3f);
        out += B64URL_ALPHABET.charAt((triple >> 12) & 0x3f);
        if (i + 1 < len)
            out += B64URL_ALPHABET.charAt((triple >> 6) & 0x3f);
        if (i + 2 < len)
            out += B64URL_ALPHABET.charAt(triple & 0x3f);
    }
    return out;
}
export function base64UrlToBytes(b64url) {
    const clean = b64url.replace(/=+$/, '');
    if (clean.length % 4 === 1)
        return null;
    const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
    let outIdx = 0;
    for (let i = 0; i < clean.length; i += 4) {
        const c0 = B64URL_ALPHABET.indexOf(clean.charAt(i));
        const c1 = B64URL_ALPHABET.indexOf(clean.charAt(i + 1));
        const c2 = i + 2 < clean.length ? B64URL_ALPHABET.indexOf(clean.charAt(i + 2)) : 0;
        const c3 = i + 3 < clean.length ? B64URL_ALPHABET.indexOf(clean.charAt(i + 3)) : 0;
        if (c0 < 0 || c1 < 0 || c2 < 0 || c3 < 0)
            return null;
        const triple = (c0 << 18) | (c1 << 12) | (c2 << 6) | c3;
        out[outIdx++] = (triple >> 16) & 0xff;
        if (i + 2 < clean.length)
            out[outIdx++] = (triple >> 8) & 0xff;
        if (i + 3 < clean.length)
            out[outIdx++] = triple & 0xff;
    }
    return out;
}
//# sourceMappingURL=epc.js.map